import d3, { Selection } from 'd3';
import { MeasureUnitConfig } from '../../measure-unit/MeasureUnitConfig';
import { ChartConfig } from '../data/ChartConfig';
import { TooltipConfig } from '../data/TooltipConfig';
import { ChartRendererContext } from './ChartRendererContext';
import { ChartTooltipContent } from './ChartTooltipContent';

export abstract class ChartRenderer {
  protected gElement?: Selection<SVGGElement, unknown, null, undefined>;

  protected svgElement?: d3.Selection<SVGSVGElement, unknown, null, undefined>;

  protected svgHTMLElement?: Element;

  protected config?: ChartConfig;

  protected unitConfig?: MeasureUnitConfig;

  public specifiedHeight?: number;

  el?: HTMLElement;

  tooltipContent?: ChartTooltipContent;

  public parentHeight?: number;

  public static getBandSpacing(band: d3.ScaleBand<any>, index: number, removeOuterSpacing: boolean) {
    const domainLen = band.domain().length;
    const bandWidth = band.bandwidth();
    if (domainLen === 1 || !removeOuterSpacing) {
      return bandWidth / 2;
    }
    return (bandWidth * index) / (domainLen - 1);
  }

  public get secondaryAsLine(): boolean {
    return (this.config as { secondaryAsLine?: boolean }).secondaryAsLine ?? false;
  }

  public get customTooltip(): TooltipConfig | undefined {
    return this.config?.customTooltip;
  }

  abstract render(el: HTMLElement | HTMLElement[], ctx: ChartRendererContext): void;

  public rerender(ctx: ChartRendererContext, withoutHeightRecalc?: boolean) {
    if (this.el) {
      if (!withoutHeightRecalc) {
        this.el.style.height = '';
        // wait to recalc height
        setTimeout(() => {
          if (this.el) {
            const height =
              !!this.specifiedHeight || this.specifiedHeight === 0
                ? this.specifiedHeight
                : ChartRenderer.getDimensions(this.el.parentElement?.parentElement).height;

            this.el.style.height = `${height}px`;
            this.el.textContent = '';
            this.render(this.el, ctx);
          }
        }, 0);
      } else {
        this.el.textContent = '';
        this.render(this.el, ctx);
      }
    }
  }

  public updateConfig(chartConfig: ChartConfig, updateData?: boolean) {
    const copyConfig = { ...chartConfig };
    if (!updateData) {
      delete copyConfig.data;

      delete copyConfig.value;

      delete copyConfig.additionalValue;
    }

    if (this.config) {
      this.config = Object.assign(this.config, copyConfig);
    }
  }

  protected static getDimensions(ele: HTMLElement | null | undefined): {
    height: number;
    width: number;
  } {
    if (!ele) {
      return { height: 0, width: 0 };
    }

    const computedElementStyle: CSSStyleDeclaration = window.getComputedStyle(ele);
    const paddingTop = Number.parseFloat(computedElementStyle.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(computedElementStyle.paddingBottom) || 0;
    const paddingLeft = Number.parseFloat(computedElementStyle.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(computedElementStyle.paddingRight) || 0;

    return {
      height: ele.clientHeight - paddingBottom - paddingTop,
      width: ele.clientWidth - paddingLeft - paddingRight,
    };
  }

  public shouldRenderEmptyState(): boolean {
    if (
      !this.config ||
      typeof this.config !== 'object' ||
      !Array.isArray(this.config.data) ||
      this.config.data.length === 0
    ) {
      return true;
    }

    return !this.config.data.some(
      chartDataArray =>
        Array.isArray(chartDataArray.data) && chartDataArray.data.some(item => item.value && item.value !== 0)
    );
  }
}
